import React from 'react';
import { Route, Routes, useLocation, useNavigate } from 'react-router-dom';
import AppFallback from '../../app/components/AppFallback/AppFallback';
import { useAppSelector } from '../../common/hooks/redux';
import { AppRoute } from './routes.types';
import {
  routesByRole,
  publicRoutes,
  defaultRoutesByRole,
} from './routes.config';
import { RouteEnum } from './routeEnum';
import { MainLayout } from '../layouts';

const SignIn = React.lazy(() => import('../../authorization/SignIn/SignIn'));
const SignUp = React.lazy(() => import('../../authorization/sign-up'));
const ForgotPassword = React.lazy(
  () => import('../../authorization/ForgotPassword/ForgotPassword'),
);
const ResetPassword = React.lazy(
  () => import('../../authorization/ResetPassword/ResetPassword'),
);
const ClientSignIn = React.lazy(
  () => import('../../authorization/SignIn/SignIn'),
);

const flattenAppRoutes = (routes: AppRoute[]): AppRoute[] =>
  routes.flatMap(route => {
    const childRoutes = (route.children ?? []).filter(
      child => child.route && child.element,
    );

    if (childRoutes.length > 0) {
      return childRoutes;
    }

    return route.route && route.element ? [route] : [];
  });

const AppRouter: React.FC = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const userRole = useAppSelector(
    (state: any) => state.authorization?.user?.role || 'none',
  );
  const effectiveRole = userRole === 'none' ? 'none' : 'owner';

  React.useEffect(() => {
    if (effectiveRole !== 'none') {
      if (location.pathname === '/') {
        navigate(defaultRoutesByRole[effectiveRole]);
        return;
      }
      if (
        location.pathname.includes('login') ||
        location.pathname.includes('sign-in')
      ) {
        navigate(defaultRoutesByRole[effectiveRole]);
      }
      return;
    }

    const publicRoutePaths = publicRoutes.map(route => route.route);
    if (publicRoutePaths.includes(location.pathname)) return;

    const authPages: string[] = [
      RouteEnum.SIGN_IN,
      RouteEnum.SIGN_UP,
      RouteEnum.FORGOT_PASSWORD,
      RouteEnum.RESET_PASSWORD,
      RouteEnum.CLIENT_SIGN_IN,
    ];
    if (authPages.includes(location.pathname)) return;

    navigate(RouteEnum.SIGN_IN);
  }, [effectiveRole, location.pathname, navigate]);

  const availableRoutes = flattenAppRoutes([
    ...publicRoutes.filter(route => !String(route.route).includes('/login')),
    ...(routesByRole[effectiveRole] || []),
  ]);

  return (
    <React.Suspense fallback={<AppFallback />}>
      <Routes>
        <Route path={RouteEnum.SIGN_IN} element={<SignIn />} />
        <Route path={RouteEnum.SIGN_UP} element={<SignUp />} />
        <Route path={RouteEnum.FORGOT_PASSWORD} element={<ForgotPassword />} />
        <Route path={RouteEnum.RESET_PASSWORD} element={<ResetPassword />} />
        <Route path={RouteEnum.CLIENT_SIGN_IN} element={<ClientSignIn />} />

        <Route element={<MainLayout />}>
          {availableRoutes.map((route: AppRoute) => (
            <Route
              key={route.route}
              path={route.route}
              element={<route.element />}
            />
          ))}
        </Route>
      </Routes>
    </React.Suspense>
  );
};

export default AppRouter;
